/**
 * ARCHX AI Engine — Portal Service
 * Handles all communication between the React portal and the AI engine.
 *
 * Dev:  engine runs at http://localhost:8080
 * Prod: engine runs on Cloud Run (us-central1)
 */

const ENGINE_URL =
  process.env.REACT_APP_ENGINE_URL ||
  'https://archxai-engine-758453419640.us-central1.run.app';
const ENGINE_KEY = process.env.REACT_APP_ENGINE_KEY || 'archxai-dev-secret-2026';
const GCS_BUCKET = 'archxai-portal.firebasestorage.app';

// Map portal category keys → engine ScanType values
const CATEGORY_TO_SCAN_TYPE = {
  preop3d:        'pre_op',
  waxup:          'wax_up',
  surgical:       'tissue_pre_surgical',
  photogrammetry: 'photogrammetry',
  postop:         'tissue_post_surgical',
  // preop2d → smile design photos (handled separately)
  // stlAligned, stlProvisional, revisions, facial3d → not engine scan types
};

const headers = {
  'Content-Type': 'application/json',
  'X-API-Key': ENGINE_KEY,
};

/**
 * Submit a case to the engine for processing.
 * Called after all files are uploaded to Firebase Storage.
 *
 * @param {string} caseId       - e.g. "ARCHX-00001"
 * @param {string} practiceId   - Firebase user UID
 * @param {Object} uploadedFiles - Array of {name, path, category} from handleSubmit
 * @returns {Promise<{job_id, status, message}>}
 */
export async function submitCaseToEngine(caseId, practiceId, uploadedFiles, toothLibrary, caseArch) {
  // Build scan_gcs_paths: map categories to gs:// paths
  const scanGcsPaths = {};
  const smileImagePaths = [];
  const cbctGcsPaths = [];

  // Which arch this run processes. Derived from the case type by the caller;
  // 'upper' | 'lower' | undefined (no preference). Used to pick the matching
  // scan file when a category holds both arches (fixes the wrong-arch file pick).
  const wantArch = caseArch || null;

  uploadedFiles.forEach(f => {
    const scanType = CATEGORY_TO_SCAN_TYPE[f.category];
    const gsPath = `gs://${GCS_BUCKET}/${f.path}`;

    if (scanType) {
      const existing = scanGcsPaths[scanType];
      if (!existing) {
        scanGcsPaths[scanType] = gsPath;
      } else if (wantArch && f.arch === wantArch) {
        // Prefer the file whose tagged arch matches the arch we're processing —
        // overrides a non-matching file that happened to be picked first.
        scanGcsPaths[scanType] = gsPath;
      }
    }
    // Collect pre-op 2D photos as smile design images
    if (f.category === 'preop2d') {
      smileImagePaths.push(gsPath);
    }
    // Collect CBCT / cone-beam DICOM files (a series can be many .dcm or a zip)
    if (f.category === 'cbct') {
      cbctGcsPaths.push(gsPath);
    }
  });

  // Need at least one scan to submit.
  // Fallback for test/edge uploads: if no file mapped to a known scan type but
  // files were uploaded, send the first one as a pre_op scan so the engine still
  // runs. In real cases (full category uploads) this never triggers.
  if (Object.keys(scanGcsPaths).length === 0) {
    if (uploadedFiles.length > 0) {
      scanGcsPaths['pre_op'] = `gs://${GCS_BUCKET}/${uploadedFiles[0].path}`;
    } else {
      throw new Error('No files found in upload');
    }
  }

  const body = {
    case_id:                caseId,
    clinician_id:           practiceId,
    scan_gcs_paths:         scanGcsPaths,
    smile_image_gcs_paths:  smileImagePaths,
    cbct_gcs_paths:         cbctGcsPaths,
    ...(toothLibrary ? { tooth_library: toothLibrary } : {}),
    ...(wantArch ? { arch: wantArch } : {}),
  };

  const res = await fetch(`${ENGINE_URL}/v1/cases`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(`Engine error ${res.status}: ${err.detail || res.statusText}`);
  }

  return res.json();
}

/**
 * Poll case processing status from the engine.
 * @param {string} caseId
 * @returns {Promise<{stage, progress, message, warnings, flagged_for_review, design_ready, error}>}
 */
export async function getCaseStatus(caseId) {
  const res = await fetch(`${ENGINE_URL}/v1/cases/${caseId}/status`, { headers });
  if (!res.ok) throw new Error(`Status check failed: ${res.status}`);
  return res.json();
}

/**
 * Get registration quality report for a case.
 * @param {string} caseId
 */
export async function getRegistrationQuality(caseId) {
  const res = await fetch(`${ENGINE_URL}/v1/cases/${caseId}/quality`, { headers });
  if (!res.ok) throw new Error(`Quality check failed: ${res.status}`);
  return res.json();
}

/**
 * Record a clinician correction to the AI design (feeds the learning loop).
 * @param {string} caseId
 * @param {Object} correction - {correction_type, tooth_number, before_value, after_value, magnitude}
 */
export async function recordCorrection(caseId, correction) {
  const res = await fetch(`${ENGINE_URL}/v1/cases/${caseId}/correct`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ case_id: caseId, clinician_id: 'portal', ...correction }),
  });
  if (!res.ok) throw new Error(`Correction failed: ${res.status}`);
  return res.json();
}

/**
 * Accept the AI design. Returns an export token for the STL download.
 * @param {string} caseId
 * @param {Array}  corrections - Any final corrections before acceptance
 */
export async function acceptDesign(caseId, corrections = []) {
  const res = await fetch(`${ENGINE_URL}/v1/cases/${caseId}/accept`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ case_id: caseId, clinician_id: 'portal', corrections }),
  });
  if (!res.ok) throw new Error(`Accept failed: ${res.status}`);
  return res.json();
}

/**
 * Get the active learning pipeline status.
 */
export async function getLearningStatus() {
  const res = await fetch(`${ENGINE_URL}/v1/learning/status`, { headers });
  if (!res.ok) throw new Error(`Learning status failed: ${res.status}`);
  return res.json();
}

/**
 * Health check — confirm engine is reachable.
 * @returns {Promise<boolean>}
 */
export async function checkEngineHealth() {
  try {
    const res = await fetch(`${ENGINE_URL}/v1/health`, { headers });
    const data = await res.json();
    return data.status === 'ok';
  } catch {
    return false;
  }
}
